import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, X, Calendar } from 'lucide-react';
import { usePageNavigation } from '../../lib/contexts/PageNavigationContext';
import { useLanguage } from '../../lib/i18n/LanguageContext';

const OpeningHoursOverlay = () => {
  const { isOpeningHoursOpen, setIsOpeningHoursOpen } = usePageNavigation();
  const { t } = useLanguage();

  // Close overlay when clicking backdrop
  const closeOverlay = () => {
    setIsOpeningHoursOpen(false);
  };

  // Prevent body scroll when overlay is open
  useEffect(() => {
    if (isOpeningHoursOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpeningHoursOpen]);

  // Get current status
  const getCurrentStatus = () => {
    const now = new Date();
    const reykjavikTime = new Date(now.toLocaleString('en-US', { timeZone: 'Atlantic/Reykjavik' }));
    const currentDay = reykjavikTime.getDay();
    const currentTime = reykjavikTime.toTimeString().slice(0, 5);

    const openingHours = {
      0: { open: '17:00', close: '22:00' }, // Sunday
      1: { open: '12:00', close: '22:00' }, // Monday
      2: { open: '12:00', close: '22:00' }, // Tuesday
      3: { open: '12:00', close: '22:00' }, // Wednesday
      4: { open: '12:00', close: '22:00' }, // Thursday
      5: { open: '12:00', close: '22:00' }, // Friday
      6: { open: '12:00', close: '22:00' }, // Saturday
    } as const;

    const todaysHours = openingHours[currentDay as keyof typeof openingHours];
    const isOpen = currentTime >= todaysHours.open && currentTime < todaysHours.close;

    return {
      isOpen,
      statusText: isOpen ? 'Open Now' : 'Closed',
      nextChange: isOpen
        ? `Closes at ${todaysHours.close}`
        : currentTime < todaysHours.open
          ? `Opens today at ${todaysHours.open}`
          : `Opens tomorrow at ${openingHours[((currentDay + 1) % 7) as keyof typeof openingHours].open}`,
    };
  };

  const status = getCurrentStatus();

  return (
    <AnimatePresence>
      {isOpeningHoursOpen && (
        <div className="lg:hidden">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 z-[9998]"
            onClick={closeOverlay}
          />

          {/* Opening Hours Panel */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 300,
              duration: 0.4,
            }}
            className="fixed bottom-0 left-0 right-0 z-[9999] bg-white rounded-t-3xl shadow-2xl max-h-[70vh] overflow-hidden"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            {/* Grabber Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <button
                onClick={closeOverlay}
                className="w-12 h-1 bg-neutral-300 rounded-full hover:bg-neutral-400 transition-colors"
                aria-label="Close opening hours"
              />
            </div>

            {/* Content */}
            <div className="px-6 pb-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="bg-red-100 p-2 rounded-full">
                    <Clock className="w-6 h-6 text-red-800" />
                  </div>
                  <h2 className="text-xl font-serif font-semibold text-neutral-900">
                    {t.common.footer.hours.title}
                  </h2>
                </div>
                <button
                  onClick={closeOverlay}
                  className="p-2 text-neutral-400 hover:text-neutral-600 transition-colors rounded-full hover:bg-neutral-100"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Current Status Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.3 }}
                className={`rounded-2xl p-6 mb-6 border-2 ${
                  status.isOpen ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-4 h-4 rounded-full ${
                        status.isOpen ? 'bg-green-500' : 'bg-red-500'
                      } animate-pulse`}
                    />
                    <div>
                      <h3
                        className={`font-semibold text-lg ${
                          status.isOpen ? 'text-green-800' : 'text-red-800'
                        }`}
                      >
                        {status.statusText}
                      </h3>
                      <p className={`text-sm ${status.isOpen ? 'text-green-700' : 'text-red-700'}`}>
                        {status.nextChange}
                      </p>
                    </div>
                  </div>
                  <Calendar
                    className={`w-6 h-6 ${status.isOpen ? 'text-green-600' : 'text-red-600'}`}
                  />
                </div>
              </motion.div>

              {/* Opening Hours Schedule */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.3 }}
                className="bg-neutral-50 rounded-2xl p-6 mb-6"
              >
                <h3 className="font-serif text-lg font-semibold text-neutral-900 mb-4 text-center">
                  Weekly Schedule
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-3 border-b border-neutral-200">
                    <span className="font-medium text-neutral-700">
                      {t.common.footer.hours.weekdays}
                    </span>
                    <span className="font-semibold text-neutral-900 bg-white px-3 py-1 rounded-full text-sm">
                      {t.common.footer.hours.weekdaysTime}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="font-medium text-neutral-700">
                      {t.common.footer.hours.sunday}
                    </span>
                    <span className="font-semibold text-neutral-900 bg-white px-3 py-1 rounded-full text-sm">
                      {t.common.footer.hours.sundayTime}
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default OpeningHoursOverlay;
